import React, { useEffect, useState } from "react";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import { admin_internship_analytics } from "../admin.api";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import {
  BsBriefcase,
  BsFileEarmarkText,
  BsAward,
  BsPersonCheck,
  BsGraphUp,
  BsPieChart,
  BsBuilding,
  BsBarChartLine,
} from "react-icons/bs";

const COLORS = {
  requests: "#3b82f6",
  applications: "#8b5cf6",
  completed: "#10b981",
  absorbed: "#f59e0b",
};

const PIE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#6b7280"];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "12px",
          color: "#0f172a",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          fontSize: "13px",
        }}
      >
        <p style={{ fontWeight: "bold", marginBottom: "8px", borderBottom: "1px solid #e2e8f0", paddingBottom: "4px", color: "#1e293b" }}>
          {label}
        </p>
        {payload.map((item, idx) => (
          <div key={idx} style={{ display: "flex", justifyContent: "space-between", gap: "20px", margin: "4px 0" }}>
            <span style={{ color: "#475569", display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: item.color || item.fill, display: "inline-block" }}></span>
              {item.name}:
            </span>
            <span style={{ fontWeight: "bold", color: "#0f172a" }}>{item.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function InternshipVisualization(props) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackError, setSnackError] = useState(false);
  const [visibleSeries, setVisibleSeries] = useState({
    requests: true,
    applications: true,
    completed: true,
    absorbed: true,
  });

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getInitialDates = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
    return {
      start: formatDate(start),
      end: formatDate(today)
    };
  };

  const initialDates = getInitialDates();
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);

  const fetchAnalytics = (start = startDate, end = endDate) => {
    setLoading(true);
    const params = {};
    if (start) params.start_date = start;
    if (end) params.end_date = end;

    admin_internship_analytics(params)
      .then((res) => {
        if (res?.success) {
          setData(res.data);
        } else {
          setSnackMessage(res?.error || "Failed to load internship analytics data");
          setSnackError(true);
          setSnackOpen(true);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Analytics fetch error:", err);
        setSnackMessage("Server error while loading analytics data");
        setSnackError(true);
        setSnackOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAnalytics(startDate, endDate);
  }, []);

  const handleReset = () => {
    const dates = getInitialDates();
    setStartDate(dates.start);
    setEndDate(dates.end);
    fetchAnalytics(dates.start, dates.end);
  };

  const toggleSeries = (series) => {
    setVisibleSeries((prev) => ({
      ...prev,
      [series]: !prev[series],
    }));
  };

  const handleSnackbarClose = () => {
    setSnackOpen(false);
  };

  // Safe checks & fallback data
  const totals = data?.totals || { requests: 0, applications: 0, completed: 0, absorbed: 0 };
  const monthlyTrend = data?.monthlyTrend || [];
  const topCompanies = data?.topCompanies || [];
  const topDisciplines = data?.topDisciplines || [];

  // Parse and map request status codes
  const reqStatusesToMap = [
    { value: "pending", label: "Pending" },
    { value: "submitted_for_acceptance", label: "Submitted" },
    { value: "approved", label: "Approved" },
    { value: "initially_rejected", label: "Admin Reverted" },
    { value: "reverted_to_admin", label: "Reverted to Admin" },
  ];
  const apiReqStatuses = data?.requestStatuses || [];
  const requestStatuses = reqStatusesToMap.map((item) => {
    const found = apiReqStatuses.find((statusObj) => statusObj.status === item.value);
    return { name: item.label, value: found ? Number(found.count) || 0 : 0 };
  });

  // Parse and map student application status codes
  const appStatusesToMap = [
    { value: 0, label: "Pending" },
    { value: 4, label: "Submitted for Acceptance" },
    { value: 1, label: "Approved" },
    { value: 5, label: "Reverted to Admin" },
    { value: 2, label: "Reverted to User" },
  ];
  const apiAppStatuses = data?.applicationStatuses || [];
  const applicationStatuses = appStatusesToMap.map((item) => {
    const found = apiAppStatuses.find((statusObj) => statusObj.status == item.value);
    return { name: item.label, value: found ? Number(found.count) || 0 : 0 };
  });

  return (
    <div className="container-fluid mt-4" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      <Loader open={loading} />
      <Snackbar
        open={snackOpen}
        message={snackMessage}
        error={snackError}
        close={handleSnackbarClose}
      />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1" style={{ fontWeight: "800", color: "#1e293b" }}>
            Internship Analytics Visualization
          </h4>
          <p className="text-muted small mb-0">
            Real-time dashboard monitoring internship performance, requests, student applications, and absorption rates.
          </p>
        </div>
        {/* <button
          onClick={() => fetchAnalytics(startDate, endDate)}
          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
          style={{ borderRadius: "8px", padding: "8px 16px" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
          </svg>
          Refresh Data
        </button> */}
      </div>

      {/* Date Range Filter Bar */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
        <div className="card-body p-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <span className="small text-muted text-nowrap" style={{ fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>Start Date:</span>
              <input
                type="date"
                className="form-control form-control-sm border-0"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  borderRadius: "8px",
                  backgroundColor: "#f1f5f9",
                  padding: "8px 12px",
                  fontWeight: "600",
                  color: "#334155",
                  fontSize: "13px"
                }}
              />
            </div>
            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <span className="small text-muted text-nowrap" style={{ fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>End Date:</span>
              <input
                type="date"
                className="form-control form-control-sm border-0"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  borderRadius: "8px",
                  backgroundColor: "#f1f5f9",
                  padding: "8px 12px",
                  fontWeight: "600",
                  color: "#334155",
                  fontSize: "13px"
                }}
              />
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              onClick={() => fetchAnalytics(startDate, endDate)}
              className="btn btn-sm btn-primary d-flex align-items-center gap-2"
              style={{ borderRadius: "8px", padding: "8px 18px", fontWeight: "600" }}
            >
              Apply Filter
            </button>
            <button
              onClick={handleReset}
              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
              style={{ borderRadius: "8px", padding: "8px 18px", fontWeight: "600" }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row g-4 mb-4">
        {/* Card 1: Total Requests */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="card border-0 shadow-sm h-100"
            style={{
              borderRadius: "16px",
              background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
              transition: "transform 0.2s ease-in-out",
              cursor: "pointer",
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            <div className="card-body p-4 d-flex align-items-center justify-content-between">
              <div>
                <span className="small" style={{ color: "#2563eb", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Total Requests
                </span>
                <h2 className="mt-2 mb-0" style={{ fontWeight: "800", color: "#1e3a8a" }}>
                  {totals.requests}
                </h2>
                <div className="mt-2 text-muted x-small">Company opportunities posted</div>
              </div>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  color: COLORS.requests,
                  borderRadius: "12px",
                  padding: "12px",
                  boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.1)",
                }}
              >
                <BsBriefcase size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Total Applications */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="card border-0 shadow-sm h-100"
            style={{
              borderRadius: "16px",
              background: "linear-gradient(135deg, #f5f3ff 0%, #edd9ff 100%)",
              transition: "transform 0.2s ease-in-out",
              cursor: "pointer",
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            <div className="card-body p-4 d-flex align-items-center justify-content-between">
              <div>
                <span className="small" style={{ color: "#7c3aed", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Total Applications
                </span>
                <h2 className="mt-2 mb-0" style={{ fontWeight: "800", color: "#4c1d95" }}>
                  {totals.applications}
                </h2>
                <div className="mt-2 text-muted x-small">Student enrollment forms received</div>
              </div>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  color: COLORS.applications,
                  borderRadius: "12px",
                  padding: "12px",
                  boxShadow: "0 4px 6px -1px rgba(139, 92, 246, 0.1)",
                }}
              >
                <BsFileEarmarkText size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Total Completed */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="card border-0 shadow-sm h-100"
            style={{
              borderRadius: "16px",
              background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
              transition: "transform 0.2s ease-in-out",
              cursor: "pointer",
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            <div className="card-body p-4 d-flex align-items-center justify-content-between">
              <div>
                <span className="small" style={{ color: "#059669", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Total Completed
                </span>
                <h2 className="mt-2 mb-0" style={{ fontWeight: "800", color: "#064e3b" }}>
                  {totals.completed}
                </h2>
                <div className="mt-2 text-muted x-small">Internships certified successfully</div>
              </div>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  color: COLORS.completed,
                  borderRadius: "12px",
                  padding: "12px",
                  boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.1)",
                }}
              >
                <BsAward size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Card 4: Total Absorbed */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="card border-0 shadow-sm h-100"
            style={{
              borderRadius: "16px",
              background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
              transition: "transform 0.2s ease-in-out",
              cursor: "pointer",
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            <div className="card-body p-4 d-flex align-items-center justify-content-between">
              <div>
                <span className="small" style={{ color: "#d97706", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Total Absorbed
                </span>
                <h2 className="mt-2 mb-0" style={{ fontWeight: "800", color: "#78350f" }}>
                  {totals.absorbed}
                </h2>
                <div className="mt-2 text-muted x-small">Absorbed directly into full-time roles</div>
              </div>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  color: COLORS.absorbed,
                  borderRadius: "12px",
                  padding: "12px",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.1)",
                }}
              >
                <BsPersonCheck size={24} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Trend Line Chart */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <div className=" bg-white border-0 pt-4 px-4 pb-2 d-flex flex-wrap justify-content-between align-items-center gap-3" style={{ backgroundColor: "#ffffff" }}>
              <div className="d-flex align-items-center gap-2">
                <BsBarChartLine size={20} style={{ color: COLORS.requests }} />
                <h5 className="mb-0" style={{ fontWeight: "700", color: "#1e293b" }}>
                  Monthly Growth & Lifecycle Trend
                </h5>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {Object.keys(visibleSeries).map((key) => (
                  <button
                    key={key}
                    onClick={() => toggleSeries(key)}
                    className={`btn btn-sm d-flex align-items-center gap-2`}
                    style={{
                      borderRadius: "20px",
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: "600",
                      backgroundColor: visibleSeries[key] ? `${COLORS[key]}15` : "#f3f4f6",
                      color: visibleSeries[key] ? COLORS[key] : "#9ca3af",
                      border: `1px solid ${visibleSeries[key] ? COLORS[key] : "#e5e7eb"}`,
                      transition: "all 0.2s",
                    }}
                  >
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: COLORS[key],
                        opacity: visibleSeries[key] ? 1 : 0.4,
                      }}
                    ></span>
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="card-body px-4 pb-4 pt-2">
              <div style={{ width: "100%", height: 350 }}>
                <ResponsiveContainer>
                  <BarChart data={monthlyTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend verticalAlign="top" height={36} iconType="circle" />
                    {visibleSeries.requests && (
                      <Bar
                        name="Requests"
                        dataKey="requests"
                        fill={COLORS.requests}
                        radius={[4, 4, 0, 0]}
                      />
                    )}
                    {visibleSeries.applications && (
                      <Bar
                        name="Applications"
                        dataKey="applications"
                        fill={COLORS.applications}
                        radius={[4, 4, 0, 0]}
                      />
                    )}
                    {visibleSeries.completed && (
                      <Bar
                        name="Completed"
                        dataKey="completed"
                        fill={COLORS.completed}
                        radius={[4, 4, 0, 0]}
                      />
                    )}
                    {visibleSeries.absorbed && (
                      <Bar
                        name="Absorbed"
                        dataKey="absorbed"
                        fill={COLORS.absorbed}
                        radius={[4, 4, 0, 0]}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown Donut Charts */}
      <div className="row g-4 mb-4">
        {/* Request Status Distribution */}
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <div className="bg-white border-0 pt-4 px-4 pb-2 d-flex align-items-center gap-2" style={{ backgroundColor: "#ffffff" }}>
              <BsPieChart size={20} style={{ color: "#3b82f6" }} />
              <h6 className="mb-0" style={{ fontWeight: "700", color: "#1e293b" }}>
                Request Status Distribution
              </h6>
            </div>
            <div className="card-body p-4 d-flex flex-wrap align-items-center justify-content-around">
              <div style={{ width: 160, height: 160 }}>
                {requestStatuses.some(s => s.value > 0) ? (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={requestStatuses}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {requestStatuses.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted x-small">
                    No data
                  </div>
                )}
              </div>
              <div className="d-flex flex-column gap-2" style={{ minWidth: "150px" }}>
                {requestStatuses.map((status, index) => (
                  <div key={status.name} className="d-flex align-items-center justify-content-between gap-3 text-small">
                    <span className="d-flex align-items-center gap-2 text-muted small">
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                        }}
                      ></span>
                      {status.name}
                    </span>
                    <span className="fw-bold" style={{ color: "#334155" }}>
                      {status.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Application Status Distribution */}
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <div className="bg-white border-0 pt-4 px-4 pb-2 d-flex align-items-center gap-2" style={{ backgroundColor: "#ffffff" }}>
              <BsPieChart size={20} style={{ color: "#8b5cf6" }} />
              <h6 className="mb-0" style={{ fontWeight: "700", color: "#1e293b" }}>
                Application Status Distribution
              </h6>
            </div>
            <div className="card-body p-4 d-flex flex-wrap align-items-center justify-content-around">
              <div style={{ width: 160, height: 160 }}>
                {applicationStatuses.some(s => s.value > 0) ? (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={applicationStatuses}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {applicationStatuses.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted x-small">
                    No data
                  </div>
                )}
              </div>
              <div className="d-flex flex-column gap-2" style={{ minWidth: "150px" }}>
                {applicationStatuses.map((status, index) => (
                  <div key={status.name} className="d-flex align-items-center justify-content-between gap-3 text-small">
                    <span className="d-flex align-items-center gap-2 text-muted small">
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                        }}
                      ></span>
                      {status.name}
                    </span>
                    <span className="fw-bold" style={{ color: "#334155" }}>
                      {status.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Companies & Top Disciplines */}
      <div className="row g-4 mb-5">
        {/* Top Companies Chart */}
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <div className="bg-white border-0 pt-4 px-4 pb-2 d-flex align-items-center gap-2" style={{ backgroundColor: "#ffffff" }}>
              <BsBuilding size={18} style={{ color: "#10b981" }} />
              <h6 className="mb-0" style={{ fontWeight: "700", color: "#1e293b" }}>
                Top 5 Companies by Requests
              </h6>
            </div>
            <div className="card-body px-4 pb-4 pt-2">
              <div style={{ width: "100%", height: 240 }}>
                {topCompanies.length > 0 ? (
                  <ResponsiveContainer>
                    <BarChart
                      layout="vertical"
                      data={topCompanies}
                      margin={{ top: 10, right: 10, left: 30, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <YAxis
                        type="category"
                        dataKey="company_name"
                        stroke="#475569"
                        fontSize={11}
                        width={100}
                        tickFormatter={(val) => (val.length > 15 ? `${val.substring(0, 15)}...` : val)}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total_requests" name="Total Requests" fill={COLORS.completed} radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted x-small">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Top Disciplines Chart */}
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <div className="bg-white border-0 pt-4 px-4 pb-2 d-flex align-items-center gap-2" style={{ backgroundColor: "#ffffff" }}>
              <BsGraphUp size={18} style={{ color: "#f59e0b" }} />
              <h6 className="mb-0" style={{ fontWeight: "700", color: "#1e293b" }}>
                Top 5 Requested Disciplines
              </h6>
            </div>
            <div className="card-body px-4 pb-4 pt-2">
              <div style={{ width: "100%", height: 240 }}>
                {topDisciplines.length > 0 ? (
                  <ResponsiveContainer>
                    <BarChart
                      layout="vertical"
                      data={topDisciplines}
                      margin={{ top: 10, right: 10, left: 30, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <YAxis
                        type="category"
                        dataKey="internship_discipline"
                        stroke="#475569"
                        fontSize={11}
                        width={100}
                        tickFormatter={(val) => (val.length > 15 ? `${val.substring(0, 15)}...` : val)}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total_requests" name="Total Requests" fill={COLORS.absorbed} radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted x-small">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
